package com.klantenstop.web.components.search

import androidx.compose.runtime.Composable
import com.klantenstop.web.components.H2Heading
import com.klantenstop.web.data.models.domain.Category
import com.klantenstop.web.data.models.domain.CuratedCity
import com.klantenstop.web.data.models.domain.Province
import com.klantenstop.web.data.models.domain.Radius
import com.klantenstop.web.data.models.domain.SearchParams
import com.klantenstop.web.navigation.Navigation
import com.klantenstop.web.styles.Typography
import com.klantenstop.web.utils.fromUrlSlugDisplay
import com.klantenstop.web.utils.toUrlSlug
import com.materialkobweb.components.widgets.DsCard
import com.materialkobweb.styles.MaterialColorVars
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul

private const val GRID_COLUMN_COUNT = 3

private sealed class GridPlace {
    abstract val label: String
    abstract val searchQuery: String
    abstract val locationSlug: String?
    abstract val isSelected: Boolean

    data class ProvincePlace(val province: Province, override val isSelected: Boolean) : GridPlace() {
        override val label = province.displayName
        override val searchQuery = province.displayName
        override val locationSlug = province.citySearchSlug
    }

    data class CityPlace(val city: CuratedCity, override val isSelected: Boolean) : GridPlace() {
        override val label = city.name
        override val searchQuery = city.slug.takeIf { it.isNotBlank() }?.fromUrlSlugDisplay() ?: city.name
        override val locationSlug = city.slug.takeIf { it.isNotBlank() }
    }
}

@Composable
fun CuratedLocationGrid(
    category: Category,
    curatedCities: List<CuratedCity>,
    currentLocationSlug: String,
    radius: Radius,
    breakpoint: Breakpoint,
    modifier: Modifier = Modifier
) {
    if (curatedCities.isEmpty()) return

    val normalizedQuery = currentLocationSlug.trim().toUrlSlug()
    val matchedCity = curatedCities.find { it.slug.isNotBlank() && it.slug == normalizedQuery }
        ?: curatedCities.find { it.name.toUrlSlug() == normalizedQuery }
    // Matches against `citySearchSlug` (not `displayName`/`slug`), consistent with the backend's
    // `stad` resolution -- a bare province name/slug (e.g. "antwerpen") now always resolves to the
    // same-named curated city, if one exists, never the province.
    val matchedProvinceDirectly = Province.entries.find { it.citySearchSlug == normalizedQuery }
    val matchedProvince = matchedCity?.province ?: matchedProvinceDirectly

    val provincePlaces = Province.entries.map { province ->
        GridPlace.ProvincePlace(province, isSelected = province == matchedProvinceDirectly)
    }

    val cityPool = if (matchedProvince != null) {
        curatedCities.filter { it.province == matchedProvince }
    } else {
        curatedCities.filter { it.isFeatured }
    }
    val cityPlaces = cityPool
        .sortedBy { it.name }
        .map { city -> GridPlace.CityPlace(city, isSelected = city.id == matchedCity?.id) }

    DsCard(
        modifier = modifier.fillMaxWidth(),
        contentPadding = 22.px,
        borderLineStyle = LineStyle.None,
        backgroundColor = MaterialColorVars.SurfaceContainer.value()
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            H2Heading(
                modifier = Modifier.fillMaxWidth().textAlign(TextAlign.Center),
                typography = Typography.TitleMedium,
                text = "${category.name} in een andere regio",
                color = MaterialColorVars.OnSurface.value()
            )

            CuratedLocationCssGrid(
                category = category,
                radius = radius,
                breakpoint = breakpoint,
                provincePlaces = provincePlaces,
                cityPlaces = cityPlaces
            )
        }
    }
}

@Composable
private fun CuratedLocationCssGrid(
    category: Category,
    radius: Radius,
    breakpoint: Breakpoint,
    provincePlaces: List<GridPlace.ProvincePlace>,
    cityPlaces: List<GridPlace.CityPlace>
) {
    // Deterministic column split: provinces always own column 1, cities fill columns 2 and 3.
    val column2 = cityPlaces.take((cityPlaces.size + 1) / 2)
    val column3 = cityPlaces.drop((cityPlaces.size + 1) / 2)

    Div(
        attrs = Modifier.fillMaxWidth().padding(top = 16.px).styleModifier {
            if (breakpoint > Breakpoint.SM) {
                property("display", "grid")
                property("grid-template-columns", "repeat($GRID_COLUMN_COUNT, 1fr)")
                property("column-gap", "32px")
            }
        }.toAttrs()
    ) {
        listOf(provincePlaces, column2, column3).forEach { column ->
            CuratedLocationColumn(category, radius, column)
        }
    }
}

@Composable
private fun CuratedLocationColumn(category: Category, radius: Radius, places: List<GridPlace>) {
    Ul(
        attrs = Modifier.fillMaxWidth().styleModifier {
            property("list-style", "none")
            property("margin", "0")
            property("padding", "0")
        }.toAttrs()
    ) {
        places.forEach { place ->
            Li {
                CuratedLocationLink(category, radius, place)
            }
        }
    }
}

@Composable
private fun CuratedLocationLink(category: Category, radius: Radius, place: GridPlace) {
    Link(
        variant = UncoloredLinkVariant,
        path = Navigation.Screen.Search(
            SearchParams(
                query = place.searchQuery,
                category = category,
                radius = radius,
                locationSlug = place.locationSlug
            )
        ).route,
        modifier = Modifier
            .fillMaxWidth()
            .color(
                if (place.isSelected) MaterialColorVars.Primary.value() else MaterialColorVars.OnSurface.value()
            )
            .attrsModifier { if (place.isSelected) attr("aria-current", "page") }
    ) {
        SpanText(
            modifier = Modifier.then(Typography.BodyMedium.toModifier()),
            text = "${category.name} in ${place.label}"
        )
    }
}
